import { ArrowPathIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { BPOHistoryModal } from '@/components/command-center/BPOHistoryModal';
import { useCallArtifact } from '@/hooks/useCallArtifact';
import {
  useGetBpoSummaryQuery,
  useGetGlobalEnhancedBlandCallWorkflowsQuery,
  useGetMedicalRecordsBpoSummaryQuery,
} from '@/services/api/blandService';
import { useGetCoverageQuery } from '@/services/api/coverageService';
import { CoverageCategory } from '@/services/types/coverage-types';
import { MedicalRecordsBPOSummaryRow } from '@/types/enhanced-bland-call';
import {
  extractCallId,
  extractUserName,
  findLastCallId,
} from '@/utils/telephony';

type BPOTabType = 'coverage' | 'medical_records';

// Separate component (module scope) to avoid defining components during render
const CallTypeCell: React.FC<{ run: any; row: any }> = ({ run, row }) => {
  const coverageId: string | undefined = (row as any)?.coverage_id;
  const matterId: string | undefined = (row as any)?.matter_id;
  const shouldFetch = Boolean(coverageId && matterId);
  // Call hook unconditionally (rules-of-hooks), gate network with skip
  const { data: cov } = useGetCoverageQuery(
    { matterId: matterId || '', coverageId: coverageId || '' },
    { skip: !shouldFetch },
  );

  const groupName: string =
    run?.task_summary?.group_name || run?.task_summary?.group_canonical || '';
  const g = groupName.toLowerCase();
  if (g.includes('1p')) return <span>1P</span>;
  if (g.includes('3p')) return <span>3P</span>;

  if (cov?.category === CoverageCategory.FIRST_PARTY) return <span>1P</span>;
  if (cov?.category === CoverageCategory.THIRD_PARTY) return <span>3P</span>;
  return <span />;
};

const formatIso = (iso: string | null | undefined): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return String(iso);
  }
};

// Standalone component to render the last caller's username (Dialpad or RingCentral)
export const LastCallerName: React.FC<{ actions?: unknown[] }> = ({
  actions,
}) => {
  const [name, setName] = useState<string>('');
  const { resolveArtifact } = useCallArtifact();

  const { callId, provider, fromNumber } = findLastCallId(actions);

  // First check if the user name is directly available in the action metadata
  const inlineUserName = useMemo(() => {
    if (!Array.isArray(actions)) return null;
    const entry = [...actions].reverse().find((a) => {
      if (!a || typeof a !== 'object') return false;
      const t = String((a as { type?: unknown }).type || '').toLowerCase();
      return t === 'call_started';
    }) as Record<string, unknown> | undefined;
    if (!entry) return null;
    return extractUserName(entry);
  }, [actions]);

  useEffect(() => {
    if (inlineUserName) {
      setName(inlineUserName);
      return;
    }
    if (!callId) return;
    (async () => {
      try {
        const artifact = await resolveArtifact(callId, provider, fromNumber || undefined);
        if (artifact?.user_name) setName(artifact.user_name);
      } catch {
        // ignore
      }
    })();
  }, [callId, provider, fromNumber, resolveArtifact, inlineUserName]);

  return name ? <span>{name}</span> : null;
};

export const BPOCommandCenter: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightWorkflowId = searchParams.get('highlight');
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);

  // Detect tab from URL param, default to 'coverage'
  const urlTabType = searchParams.get('type') as BPOTabType | null;
  const [activeTab, setActiveTab] = useState<BPOTabType>(
    urlTabType === 'medical_records' ? 'medical_records' : 'coverage',
  );

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [range, setRange] = useState<'24h' | '7d' | 'custom'>('24h');
  const [customStart, setCustomStart] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [sortBy, setSortBy] = useState<
    | 'matter_name'
    | 'first_time_requested'
    | 'phone_number_to'
    | 'first_time_called'
    | 'from_number'
    | 'completed_call_success'
    | 'completed_call_outcome'
    | 'calls_made'
    | 'provider_name'
    | 'outcome'
  >('first_time_requested');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyMatterId, setHistoryMatterId] = useState<string>('');
  const [historyWorkflowId, setHistoryWorkflowId] = useState<string>('');

  const startParam = useMemo(() => {
    if (range === 'custom' && customStart) return customStart;
    const now = Date.now();
    const ms = range === '7d' ? 7 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
    return new Date(now - ms).toISOString();
  }, [range, customStart]);

  // Coverage BPO data
  const {
    data,
    isLoading,
    refetch: refetchCoverage,
  } = useGetBpoSummaryQuery({
    start: startParam,
  });

  // Medical Records BPO data
  const {
    data: medicalRecordsData,
    isLoading: medicalRecordsLoading,
    refetch: refetchMedicalRecords,
  } = useGetMedicalRecordsBpoSummaryQuery({
    start: startParam,
  });

  const refetch = () => {
    if (activeTab === 'coverage') {
      refetchCoverage();
    } else {
      refetchMedicalRecords();
    }
  };

  const { data: globalWorkflows } =
    useGetGlobalEnhancedBlandCallWorkflowsQuery();
  const { resolveArtifact } = useCallArtifact();

  const workflowById = useMemo(() => {
    const list: any[] = (globalWorkflows as any)?.workflows || [];
    return list.reduce(
      (acc: Record<string, any>, w: any) => {
        if (w && w.workflow_id) acc[w.workflow_id] = w;
        return acc;
      },
      {} as Record<string, any>,
    );
  }, [globalWorkflows]);

  const pickLatestTerminalOutcome = (
    actions: any[] | undefined,
  ): { outcome: string | null; source: 'human' | 'agent' | 'unknown' } => {
    if (!Array.isArray(actions) || actions.length === 0) {
      return { outcome: null, source: 'unknown' };
    }
    const isTerminal = (a: any) => {
      const t = String(a?.type || '').toLowerCase();
      return t === 'completed' || t === 'failed' || t === 'cancelled';
    };
    const genBy = (a: any) =>
      String(a?.metadata?.generated_by || '').toLowerCase();

    // Prefer the last human terminal action by array order (authoritative), regardless of timestamps
    for (let i = actions.length - 1; i >= 0; i -= 1) {
      const a = actions[i];
      if (isTerminal(a) && genBy(a) === 'human') {
        const outcome =
          (a?.metadata?.call_outcome as string) ||
          String(a?.type || '') ||
          null;
        return { outcome, source: 'human' };
      }
    }
    // No human terminal found; fall back to the last terminal action of any source
    for (let i = actions.length - 1; i >= 0; i -= 1) {
      const a = actions[i];
      if (isTerminal(a)) {
        const outcome =
          (a?.metadata?.call_outcome as string) ||
          String(a?.type || '') ||
          null;
        const source =
          genBy(a) === 'agent'
            ? 'agent'
            : genBy(a) === 'human'
              ? 'human'
              : 'unknown';
        return { outcome, source };
      }
    }
    return { outcome: null, source: 'unknown' };
  };

  useEffect(() => {
    let intervalId: number | null = null;
    if (autoRefresh) {
      intervalId = window.setInterval(() => refetch(), 10000);
    }
    return () => {
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, [autoRefresh, refetch]);

  // Scroll to highlighted row when data loads
  useEffect(() => {
    if (highlightWorkflowId && highlightedRowRef.current && !isLoading) {
      highlightedRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      // Clear highlight param after scrolling
      setTimeout(() => {
        const params = new URLSearchParams(searchParams);
        params.delete('highlight');
        setSearchParams(params, { replace: true });
      }, 3000);
    }
  }, [highlightWorkflowId, isLoading, searchParams, setSearchParams]);

  const rows = useMemo(() => {
    const base = data?.rows || [];
    const sorted = [...base].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const av = (a as any)[sortBy];
      const bv = (b as any)[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1 * dir;
      if (bv == null) return -1 * dir;
      // Try date compare for ISO fields
      if (sortBy === 'first_time_requested' || sortBy === 'first_time_called') {
        const ad = new Date(av as string).getTime();
        const bd = new Date(bv as string).getTime();
        return (ad - bd) * dir;
      }
      // Boolean ordering
      if (sortBy === 'completed_call_success') {
        return ((av === true ? 1 : 0) - (bv === true ? 1 : 0)) * dir;
      }
      // String compare
      return String(av).localeCompare(String(bv)) * dir;
    });
    return sorted;
  }, [data, sortBy, sortDir]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r: any) => {
      const name = String(r?.matter_name || '').toLowerCase();
      const id = String(r?.matter_id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [rows, search]);

  // Medical Records rows processing
  const medicalRecordsRows = useMemo(() => {
    const base = medicalRecordsData?.rows || [];
    const sorted = [...base].sort(
      (a: MedicalRecordsBPOSummaryRow, b: MedicalRecordsBPOSummaryRow) => {
        const dir = sortDir === 'asc' ? 1 : -1;
        const av = (a as any)[sortBy];
        const bv = (b as any)[sortBy];
        if (av == null && bv == null) return 0;
        if (av == null) return 1 * dir;
        if (bv == null) return -1 * dir;
        if (
          sortBy === 'first_time_requested' ||
          sortBy === 'first_time_called'
        ) {
          const ad = new Date(av as string).getTime();
          const bd = new Date(bv as string).getTime();
          return (ad - bd) * dir;
        }
        return String(av).localeCompare(String(bv)) * dir;
      },
    );
    return sorted;
  }, [medicalRecordsData, sortBy, sortDir]);

  const filteredMedicalRecordsRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return medicalRecordsRows;
    return medicalRecordsRows.filter((r: MedicalRecordsBPOSummaryRow) => {
      const name = String(r?.matter_name || '').toLowerCase();
      const id = String(r?.matter_id || '').toLowerCase();
      const provider = String(r?.provider_name || '').toLowerCase();
      return name.includes(q) || id.includes(q) || provider.includes(q);
    });
  }, [medicalRecordsRows, search]);

  const medicalRecordsOutcomeCounts = useMemo(() => {
    const outRows = medicalRecordsData?.rows || [];
    return outRows.reduce(
      (acc: Record<string, number>, r) => {
        const outcome = r.outcome_display || r.outcome || 'unknown';
        acc[outcome] = (acc[outcome] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [medicalRecordsData]);

  const handleOpenHistory = (matterId: string, workflowId: string) => {
    setHistoryMatterId(matterId);
    setHistoryWorkflowId(workflowId);
    setHistoryOpen(true);
  };

  const toggleSort = (field: typeof sortBy) => {
    if (field === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const outcomeCounts = useMemo(() => {
    const outRows = data?.rows || [];
    return outRows.reduce(
      (acc: Record<string, number>, r) => {
        const outcome =
          r.completed_call_outcome ||
          (r.completed_call_success == null
            ? 'unknown'
            : r.completed_call_success
              ? 'success'
              : 'not_successful');
        acc[outcome] = (acc[outcome] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [data]);

  const handleOpenCall = async (actions?: unknown[]) => {
    const { callId, provider } = findLastCallId(actions);
    if (!callId) {
      toast.error('No call ID found');
      return;
    }
    try {
      const artifact = await resolveArtifact(callId, provider);
      // Prefer linking to artifacts page if we have artifact_id and matter_id
      if (artifact?.artifact_id && artifact?.matter_id) {
        window.open(
          `/matters/${artifact.matter_id}/artifacts/${artifact.artifact_id}`,
          '_blank',
        );
        return;
      }
      // Fall back to /call/ page if no artifact
      const convoId = artifact?.conversation_id;
      if (convoId) {
        window.open(`/call/${convoId}`, '_blank');
        return;
      }
      toast.error('No transcript available for this call');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      toast.error('Failed to open call transcript', { description: message });
    }
  };

  const findLastCallIdFromActions = (actions?: unknown[]): string | null => {
    return findLastCallId(actions).callId;
  };

  const pickLatestHumanEscalation = (actions?: unknown[]): string | null => {
    if (!Array.isArray(actions)) return null;
    const entry = [...actions].reverse().find((a) => {
      if (!a || typeof a !== 'object') return false;
      const meta = (a as { metadata?: Record<string, unknown> }).metadata || {};
      const gen =
        typeof meta.generated_by === 'string'
          ? (meta.generated_by as string).toLowerCase()
          : '';
      const issues =
        typeof meta.call_issues === 'string'
          ? (meta.call_issues as string).trim()
          : '';
      return gen === 'human' && issues.length > 0;
    }) as { metadata?: Record<string, unknown> } | undefined;
    if (!entry) return null;
    const meta = entry.metadata || {};
    const issues =
      typeof meta.call_issues === 'string'
        ? (meta.call_issues as string).trim()
        : '';
    return issues || null;
  };

  const extractActionTimestamp = (a: {
    requested_at?: unknown;
    received_at?: unknown;
    expired_at?: unknown;
    cancelled_at?: unknown;
  }): string | null => {
    const ts = [
      a.received_at,
      a.requested_at,
      a.expired_at,
      a.cancelled_at,
    ].find((v) => typeof v === 'string') as string | undefined;
    return ts || null;
  };

  const getLatestTerminalOutcomeAt = (actions?: unknown[]): string | null => {
    if (!Array.isArray(actions)) return null;
    const entry = [...actions].reverse().find((a) => {
      if (!a || typeof a !== 'object') return false;
      const t = String((a as { type?: unknown }).type || '').toLowerCase();
      return t === 'completed' || t === 'failed' || t === 'cancelled';
    }) as
      | {
          requested_at?: unknown;
          received_at?: unknown;
          expired_at?: unknown;
          cancelled_at?: unknown;
        }
      | undefined;
    if (!entry) return null;
    return extractActionTimestamp(entry);
  };

  const getLatestHumanEscalationAt = (actions?: unknown[]): string | null => {
    if (!Array.isArray(actions)) return null;
    const entry = [...actions].reverse().find((a) => {
      if (!a || typeof a !== 'object') return false;
      const meta = (a as { metadata?: Record<string, unknown> }).metadata || {};
      const gen =
        typeof meta.generated_by === 'string'
          ? (meta.generated_by as string).toLowerCase()
          : '';
      const issues =
        typeof meta.call_issues === 'string'
          ? (meta.call_issues as string).trim()
          : '';
      return gen === 'human' && issues.length > 0;
    }) as
      | {
          requested_at?: unknown;
          received_at?: unknown;
          expired_at?: unknown;
          cancelled_at?: unknown;
        }
      | undefined;
    if (!entry) return null;
    return extractActionTimestamp(entry);
  };

  const getLastCaller = (actions?: unknown[]): string | null => {
    if (!Array.isArray(actions)) return null;
    const entry = [...actions].reverse().find((a) => {
      if (!a || typeof a !== 'object') return false;
      const t = String((a as { type?: unknown }).type || '').toLowerCase();
      return t === 'call_started';
    }) as { metadata?: Record<string, unknown> } | undefined;
    const meta = entry?.metadata || {};
    const from =
      typeof meta.from_number === 'string' ? (meta.from_number as string) : '';
    return from || null;
  };

  const getLastCallAt = (runObj: unknown): string | null => {
    if (!runObj || typeof runObj !== 'object') return null;
    const actions = (runObj as { bpo_actions?: unknown[] }).bpo_actions;
    if (!Array.isArray(actions)) return null;
    const callStarted = [...actions].reverse().find((a) => {
      if (!a || typeof a !== 'object') return false;
      const t = String((a as { type?: unknown }).type || '').toLowerCase();
      return t === 'call_started';
    }) as
      | {
          requested_at?: unknown;
          received_at?: unknown;
          expired_at?: unknown;
          cancelled_at?: unknown;
        }
      | undefined;
    if (!callStarted) return null;
    return extractActionTimestamp(callStarted);
  };

  const getFirstCallAt = (runObj: unknown): string | null => {
    if (!runObj || typeof runObj !== 'object') return null;
    const actions = (runObj as { bpo_actions?: unknown[] }).bpo_actions;
    if (!Array.isArray(actions)) return null;
    const callStarted = actions.find((a) => {
      if (!a || typeof a !== 'object') return false;
      const t = String((a as { type?: unknown }).type || '').toLowerCase();
      return t === 'call_started';
    }) as
      | {
          requested_at?: unknown;
          received_at?: unknown;
          expired_at?: unknown;
          cancelled_at?: unknown;
        }
      | undefined;
    if (!callStarted) return null;
    return extractActionTimestamp(callStarted);
  };

  const getCallsMade = (runObj: unknown): number | null => {
    if (!runObj || typeof runObj !== 'object') return null;
    const results =
      (runObj as { call_results?: Array<unknown> }).call_results || [];
    if (Array.isArray(results) && results.length > 0) {
      return results.length;
    }
    const actions = (runObj as { bpo_actions?: unknown[] }).bpo_actions;
    if (Array.isArray(actions)) {
      return actions.filter(
        (a) =>
          a &&
          typeof a === 'object' &&
          String((a as { type?: unknown }).type || '').toLowerCase() ===
            'call_started',
      ).length;
    }
    return null;
  };

  const currentIsLoading =
    activeTab === 'coverage' ? isLoading : medicalRecordsLoading;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="flex-1 max-w-full overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <TableCellsIcon className="h-5 w-5 text-blue-600" />
                  BPO Command Center
                </h1>
                <label
                  htmlFor="auto_refresh"
                  className="flex items-center gap-2 text-xs"
                >
                  <input
                    id="auto_refresh"
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Auto-refresh (10s)
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={
                    activeTab === 'medical_records'
                      ? 'Search matter, ID, or provider'
                      : 'Search matter name or ID'
                  }
                  className="text-xs border border-gray-300 rounded px-2 py-1 w-56"
                  aria-label="Search"
                />
                <select
                  value={range}
                  onChange={(e) => setRange(e.target.value as any)}
                  className="text-xs border border-gray-300 rounded px-2 py-1"
                  aria-label="Time range"
                >
                  <option value="24h">Last 24h</option>
                  <option value="7d">Last 7 days</option>
                  <option value="custom">Custom start…</option>
                </select>
                {range === 'custom' && (
                  <input
                    type="text"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    placeholder="YYYY-MM-DDTHH:mm:ssZ"
                    className="text-xs border border-gray-300 rounded px-2 py-1 w-64"
                    aria-label="Custom start time ISO"
                  />
                )}
                <button
                  type="button"
                  onClick={() => refetch()}
                  disabled={currentIsLoading}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  <ArrowPathIcon
                    className={`h-3 w-3 ${currentIsLoading ? 'animate-spin' : ''}`}
                  />
                  Refresh
                </button>
              </div>
            </div>
          </div>
          {/* Tabs */}
          <div className="px-4 py-2 flex gap-1 bg-gray-50">
            <button
              type="button"
              onClick={() => setActiveTab('coverage')}
              className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
                activeTab === 'coverage'
                  ? 'bg-white text-blue-600 border-t border-l border-r border-gray-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Coverage BPO ({data?.count ?? 0})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('medical_records')}
              className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
                activeTab === 'medical_records'
                  ? 'bg-white text-blue-600 border-t border-l border-r border-gray-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Medical Records BPO ({medicalRecordsData?.count ?? 0})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 pt-0">
          {activeTab === 'coverage' && (
            <div className="bg-white rounded border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('matter_name')}
                        >
                          Matter
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('first_time_requested')}
                        >
                          First Time Requested
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('phone_number_to')}
                        >
                          Phone Number To
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Call Type
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('first_time_called')}
                        >
                          First Time Called
                        </button>
                      </th>

                      <th className="px-3 py-2 text-left font-semibold">
                        Last Call
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('completed_call_outcome')}
                        >
                          Call Outcome
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Outcome At
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('calls_made')}
                        >
                          Calls
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Evaluation From
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Call Escalation
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Escalation At
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Last Caller
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Initiated By
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        History
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Links
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td
                          colSpan={15}
                          className="px-3 py-10 text-center text-gray-500"
                        >
                          Loading…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={15}
                          className="px-3 py-10 text-center text-gray-500"
                        >
                          No data
                        </td>
                      </tr>
                    ) : filteredRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={15}
                          className="px-3 py-10 text-center text-gray-500"
                        >
                          No matches
                        </td>
                      </tr>
                    ) : (
                      filteredRows.map((r) => {
                        const run = workflowById[r.workflow_id];
                        const latestCallId = findLastCallIdFromActions(
                          run?.bpo_actions,
                        );
                        const coverageIdForLinks: string | undefined =
                          (r as any)?.coverage_id ||
                          (run?.task_summary?.coverage_id as
                            | string
                            | undefined) ||
                          (run as any)?.task?.coverage?.id ||
                          undefined;
                        const bpoFormUrl = `/bpo/outcome?matter_id=${encodeURIComponent(r.matter_id)}&workflow_id=${encodeURIComponent(r.workflow_id)}${r.phone_number_to ? `&to_number=${encodeURIComponent(r.phone_number_to)}` : ''}${r.from_number ? `&from_number=${encodeURIComponent(r.from_number)}` : ''}${latestCallId ? `&call_id=${encodeURIComponent(latestCallId)}` : ''}${coverageIdForLinks ? `&coverage_id=${encodeURIComponent(coverageIdForLinks)}` : ''}`;
                        const { outcome: derivedOutcome, source } =
                          pickLatestTerminalOutcome(run?.bpo_actions);
                        const outcomeLabel =
                          derivedOutcome ||
                          r.completed_call_outcome ||
                          (r.completed_call_success == null
                            ? ''
                            : r.completed_call_success
                              ? 'success'
                              : 'not_successful');
                        // coverageIdForLinks computed above
                        const isHighlighted =
                          highlightWorkflowId === r.workflow_id;
                        return (
                          <tr
                            key={r.workflow_id}
                            ref={isHighlighted ? highlightedRowRef : null}
                            className={`border-t border-gray-100 transition-colors ${
                              isHighlighted
                                ? 'bg-yellow-100 ring-2 ring-yellow-400'
                                : ''
                            }`}
                          >
                            <td className="px-3 py-2 text-gray-900">
                              {r.matter_id ? (
                                <Link
                                  to={`/matters/${r.matter_id}`}
                                  className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {r.matter_name || r.matter_id}
                                </Link>
                              ) : (
                                r.matter_name || ''
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {formatIso(r.first_time_requested)}
                            </td>
                            <td className="px-3 py-2">
                              {r.phone_number_to || ''}
                            </td>
                            <td className="px-3 py-2">
                              <CallTypeCell run={run} row={r} />
                            </td>
                            <td className="px-3 py-2">
                              {formatIso(
                                getFirstCallAt(run) || r.first_time_called,
                              )}
                            </td>

                            <td className="px-3 py-2">
                              {formatIso(getLastCallAt(run))}
                            </td>
                            <td className="px-3 py-2">{outcomeLabel}</td>
                            <td className="px-3 py-2">
                              {formatIso(
                                getLatestTerminalOutcomeAt(run?.bpo_actions),
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {getCallsMade(run) ?? r.calls_made ?? ''}
                            </td>
                            <td className="px-3 py-2">
                              {source === 'human'
                                ? 'Human'
                                : source === 'agent'
                                  ? 'AI'
                                  : ''}
                            </td>
                            <td
                              className="px-3 py-2 max-w-[280px] truncate"
                              title={
                                pickLatestHumanEscalation(run?.bpo_actions) ||
                                r.call_issues ||
                                ''
                              }
                            >
                              {pickLatestHumanEscalation(run?.bpo_actions) ||
                                r.call_issues ||
                                ''}
                            </td>
                            <td className="px-3 py-2">
                              {formatIso(
                                getLatestHumanEscalationAt(run?.bpo_actions),
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <LastCallerName actions={run?.bpo_actions} />
                            </td>
                            <td className="px-3 py-2">
                              {(r as any).initiated_by_user_name || ''}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() =>
                                  handleOpenHistory(r.matter_id, r.workflow_id)
                                }
                                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                                aria-label="Open full history"
                              >
                                History
                              </button>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap space-x-2">
                              <a
                                href={bpoFormUrl}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                                target="_blank"
                                rel="noreferrer"
                              >
                                BPO form
                              </a>
                              <a
                                href={`/bpo/start?matter_id=${encodeURIComponent(r.matter_id)}&workflow_id=${encodeURIComponent(r.workflow_id)}${r.phone_number_to ? `&to_number=${encodeURIComponent(r.phone_number_to)}` : ''}${coverageIdForLinks ? `&coverage_id=${encodeURIComponent(coverageIdForLinks)}` : ''}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Start call
                              </a>
                              {(() => {
                                const runObj = workflowById[r.workflow_id];
                                const callId = findLastCallIdFromActions(
                                  runObj?.bpo_actions,
                                );
                                if (!callId) return null;
                                return (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleOpenCall(runObj?.bpo_actions)
                                    }
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                    aria-label="Open call transcript"
                                  >
                                    Call transcript
                                  </button>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 text-xs text-gray-700 border-t border-gray-100 flex items-center justify-between">
                <div>Showing {rows.length} rows</div>
                <div className="flex items-center gap-3">
                  {Object.entries(outcomeCounts).map(([k, v]) => (
                    <span
                      key={k}
                      className="px-2 py-0.5 rounded bg-gray-100 border border-gray-200"
                    >
                      {k}: {v}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Medical Records BPO Table */}
          {activeTab === 'medical_records' && (
            <div className="bg-white rounded border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('matter_name')}
                        >
                          Matter
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('provider_name')}
                        >
                          Provider
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('first_time_requested')}
                        >
                          Requested
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('phone_number_to')}
                        >
                          Phone To
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('first_time_called')}
                        >
                          First Called
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('outcome')}
                        >
                          Outcome
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Details
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => toggleSort('calls_made')}
                        >
                          Calls
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Initiated By
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicalRecordsLoading ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-10 text-center text-gray-500"
                        >
                          Loading…
                        </td>
                      </tr>
                    ) : medicalRecordsRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-10 text-center text-gray-500"
                        >
                          No data
                        </td>
                      </tr>
                    ) : filteredMedicalRecordsRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-10 text-center text-gray-500"
                        >
                          No matches
                        </td>
                      </tr>
                    ) : (
                      filteredMedicalRecordsRows.map(
                        (r: MedicalRecordsBPOSummaryRow) => {
                          const isHighlighted =
                            highlightWorkflowId === r.workflow_id;
                          // Build outcome details display
                          const details = r.outcome_details || {};
                          const detailParts: string[] = [];
                          if (details.delivery_method) {
                            detailParts.push(
                              `Delivery: ${details.delivery_method}`,
                            );
                          }
                          if (details.estimated_timeframe) {
                            detailParts.push(
                              `ETA: ${details.estimated_timeframe}`,
                            );
                          }
                          if (details.correct_phone_number) {
                            detailParts.push(
                              `Correct #: ${details.correct_phone_number}`,
                            );
                          }
                          if (details.resend_method) {
                            detailParts.push(
                              `Resend: ${details.resend_method}`,
                            );
                          }
                          if (details.reason) {
                            detailParts.push(`Reason: ${details.reason}`);
                          }
                          if (details.unable_to_reach_outcome) {
                            detailParts.push(details.unable_to_reach_outcome);
                          }
                          if (details.issue_type) {
                            detailParts.push(`Issue: ${details.issue_type}`);
                          }

                          // Outcome emoji
                          const outcomeEmoji: Record<string, string> = {
                            records_sent: '✅',
                            request_in_processing: '⏳',
                            wrong_number: '📞',
                            request_not_received: '📭',
                            patient_not_found: '❓',
                            unable_to_reach: '📵',
                            other_issue: '⚠️',
                          };

                          return (
                            <tr
                              key={r.workflow_id}
                              ref={isHighlighted ? highlightedRowRef : null}
                              className={`border-t border-gray-100 transition-colors ${
                                isHighlighted
                                  ? 'bg-yellow-100 ring-2 ring-yellow-400'
                                  : ''
                              }`}
                            >
                              <td className="px-3 py-2 text-gray-900">
                                {r.matter_id ? (
                                  <Link
                                    to={`/matters/${r.matter_id}/treatment`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {r.matter_name || r.matter_id}
                                  </Link>
                                ) : (
                                  r.matter_name || ''
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {r.provider_name || '—'}
                              </td>
                              <td className="px-3 py-2">
                                {formatIso(r.first_time_requested)}
                              </td>
                              <td className="px-3 py-2">
                                {r.phone_number_to || ''}
                              </td>
                              <td className="px-3 py-2">
                                {formatIso(r.first_time_called)}
                              </td>
                              <td className="px-3 py-2">
                                <span
                                  className="inline-flex items-center gap-1"
                                  title={r.outcome || ''}
                                >
                                  {r.outcome && outcomeEmoji[r.outcome]}
                                  {r.outcome_display || r.outcome || '—'}
                                </span>
                              </td>
                              <td
                                className="px-3 py-2 max-w-[250px] truncate"
                                title={detailParts.join(' | ')}
                              >
                                {detailParts.length > 0
                                  ? detailParts.join(' | ')
                                  : '—'}
                              </td>
                              <td className="px-3 py-2">
                                {r.calls_made ?? '—'}
                              </td>
                              <td className="px-3 py-2">
                                {r.initiated_by_user_name || '—'}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap space-x-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleOpenHistory(
                                      r.matter_id,
                                      r.workflow_id,
                                    )
                                  }
                                  className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                                  aria-label="Open full history"
                                >
                                  History
                                </button>
                                {r.matter_id && (
                                  <Link
                                    to={`/matters/${r.matter_id}/treatment`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline text-xs"
                                  >
                                    Treatment
                                  </Link>
                                )}
                              </td>
                            </tr>
                          );
                        },
                      )
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 text-xs text-gray-700 border-t border-gray-100 flex items-center justify-between">
                <div>Showing {medicalRecordsRows.length} rows</div>
                <div className="flex items-center gap-3">
                  {Object.entries(medicalRecordsOutcomeCounts).map(([k, v]) => (
                    <span
                      key={k}
                      className="px-2 py-0.5 rounded bg-gray-100 border border-gray-200"
                    >
                      {k}: {v}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <BPOHistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        matterId={historyMatterId}
        workflowId={historyWorkflowId}
      />
    </div>
  );
};
